import React, { useContext, useEffect, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";

import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import Divider from "@material-ui/core/Divider";
import Collapse from "@material-ui/core/Collapse";
import Tooltip from "@material-ui/core/Tooltip";
import { makeStyles } from "@material-ui/core/styles";
import { ExpandLess, ExpandMore } from "@material-ui/icons";

import { i18n } from "../translate/i18n";
import { AuthContext } from "../context/Auth/AuthContext";
import { Can } from "../components/Can";
import {
  CampaignsIcon,
  ContactsIcon,
  ConversationsIcon,
  DashboardIcon,
  KanbanIcon,
  ReportsIcon,
  SettingsIcon,
  ScheduleIcon,
  TasksIcon,
  UsersIcon,
} from "../components/CrmIcons";

const useStyles = makeStyles((theme) => ({
  navItem: {
    position: "relative",
    minHeight: 52,
    width: "auto",
    margin: "5px 12px",
    padding: "9px 11px",
    borderRadius: 14,
    color: "var(--crm-nav-foreground, #000000)",
    transition: "background-color 160ms ease, color 160ms ease, transform 160ms ease",
    "&:hover": {
      color: "var(--crm-nav-foreground, #000000)",
      backgroundColor: "rgba(255,255,255,.09)",
      transform: "translateY(-1px)",
    },
  },
  navItemCollapsed: {
    width: 54,
    height: 54,
    margin: "5px auto",
    padding: 0,
    justifyContent: "center",
  },
  navIcon: {
    minWidth: 44,
    width: 44,
    flexShrink: 0,
    color: "inherit",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "& svg": { display: "block", flexShrink: 0, color: "inherit" },
  },
  navIconCollapsed: { minWidth: 24, width: 24 },
  navLabel: {
    fontSize: "0.92rem",
    fontWeight: 600,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  navLabelActive: { fontWeight: 800 },
  navSubItem: {
    minHeight: 42,
    marginLeft: 28,
    marginRight: 12,
    padding: "7px 10px",
    borderRadius: 11,
    "& $navIcon": { minWidth: 34, width: 34 },
  },
  navItemActive: {
    color: "#fff",
    background: "linear-gradient(145deg, rgba(118,84,255,.98), rgba(93,67,214,.94))",
    boxShadow: "0 10px 24px rgba(80,54,190,.34), inset 0 1px 0 rgba(255,255,255,.22)",
    "&::before": {
      content: '""',
      position: "absolute",
      left: -12,
      top: 13,
      bottom: 13,
      width: 3,
      borderRadius: 5,
      backgroundColor: "#d8ceff",
      boxShadow: "0 0 12px rgba(216,206,255,.9)",
    },
    "&:hover": { background: "linear-gradient(145deg, #8068ff, #6549df)" },
  },
}));

function ListItemLink(props) {
  const { icon, primary, to, className, collapsed, onClick, expanded } = props;
  const classes = useStyles();
  const location = useLocation();
  const active = to === "/" ? location.pathname === "/" : location.pathname === to || location.pathname.startsWith(`${to}/`);

  const renderLink = React.useMemo(
    () =>
      React.forwardRef((itemProps, ref) => (
        <RouterLink to={to} ref={ref} {...itemProps} />
      )),
    [to]
  );

  return (
    <li>
      <ListItem
        button
        component={renderLink}
        selected={active}
        aria-label={primary}
        aria-expanded={expanded}
        onClick={onClick}
        className={`${classes.navItem} ${collapsed ? classes.navItemCollapsed : ""} ${active ? classes.navItemActive : ""} ${className || ""}`}
      >
        {icon ? (
          <Tooltip title={primary} placement="right" arrow disableHoverListener={!collapsed}>
            <ListItemIcon className={`${classes.navIcon} ${collapsed ? classes.navIconCollapsed : ""}`}>{icon}</ListItemIcon>
          </Tooltip>
        ) : null}
        {!collapsed && <ListItemText primary={primary} classes={{ primary: `${classes.navLabel} ${active ? classes.navLabelActive : ""}` }} />}
      </ListItem>
    </li>
  );
}

const MainListItems = (props) => {
  const { drawerClose, collapsed = false } = props;
  const { user } = useContext(AuthContext);
  const classes = useStyles();
  const location = useLocation();
  const marketingActive = location.pathname === "/campaigns" || location.pathname === "/content-calendar";
  const [marketingOpen, setMarketingOpen] = useState(marketingActive);

  useEffect(() => {
    if (marketingActive) setMarketingOpen(true);
  }, [marketingActive]);

  return (
    <div onClick={drawerClose}>
      <ListItemLink
        to="/"
        primary="Panel"
        icon={<DashboardIcon />}
        collapsed={collapsed}
      />
      <ListItemLink
        to="/tickets"
        primary="Conversaciones"
        icon={<ConversationsIcon />}
        collapsed={collapsed}
      />

      <ListItemLink
        to="/kanban"
        primary="Kanban"
        icon={<KanbanIcon />}
        collapsed={collapsed}
      />

      <ListItemLink
        to="/tasks"
        primary="Tareas y agenda"
        icon={<TasksIcon />}
        collapsed={collapsed}
      />
      <ListItemLink
        to="/reports"
        primary="Reportes"
        icon={<ReportsIcon />}
        collapsed={collapsed}
      />
      <li>
        <ListItem
          button
          selected={marketingActive}
          aria-label="Marketing"
          aria-expanded={marketingOpen}
          onClick={(event) => {
            event.stopPropagation();
            setMarketingOpen((open) => !open);
          }}
          className={`${classes.navItem} ${collapsed ? classes.navItemCollapsed : ""} ${marketingActive ? classes.navItemActive : ""}`}
        >
          <Tooltip title="Marketing" placement="right" arrow disableHoverListener={!collapsed}>
            <ListItemIcon className={`${classes.navIcon} ${collapsed ? classes.navIconCollapsed : ""}`}><CampaignsIcon /></ListItemIcon>
          </Tooltip>
          {!collapsed && <>
            <ListItemText primary="Marketing" classes={{ primary: `${classes.navLabel} ${marketingActive ? classes.navLabelActive : ""}` }} />
            {marketingOpen ? <ExpandLess /> : <ExpandMore />}
          </>}
        </ListItem>
      </li>
      {!collapsed && (
        <Collapse in={marketingOpen} timeout="auto" unmountOnExit>
          <ListItemLink
            to="/campaigns"
            primary="Campañas"
            icon={<CampaignsIcon />}
            collapsed={collapsed}
            className={classes.navSubItem}
          />
          <ListItemLink
            to="/content-calendar"
            primary="Calendario de contenido"
            icon={<ScheduleIcon />}
            collapsed={collapsed}
            className={classes.navSubItem}
          />
        </Collapse>
      )}
      <Can
        role={user.profile}
        perform="drawer-admin-items:view"
        yes={() => (
          <>
            <Divider />
            <ListItemLink
              to="/users"
              primary={i18n.t("mainDrawer.listItems.users")}
              icon={<UsersIcon />}
              collapsed={collapsed}
            />
            <ListItemLink
              to="/settings"
              primary={i18n.t("mainDrawer.listItems.settings")}
              icon={<SettingsIcon />}
              collapsed={collapsed}
            />
          </>
        )}
      />
    </div>
  );
};

export default MainListItems;
